import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  HistoryResponse,
  StatusResponse,
  WireIncident,
  WireService,
} from '@shared/contract';
import { api } from '@/api/client';

export type ConnectionState = 'connecting' | 'live' | 'polling' | 'error';

export interface Transition {
  key: string;
  serviceName: string;
  from: WireService['status'];
  to: WireService['status'];
  at: number;
}

export interface DashboardData {
  services: WireService[];
  openIncidents: WireIncident[];
  incidents: WireIncident[];
  agents: StatusResponse['agents'];
  history: HistoryResponse['history'];
  loaded: boolean;
  error: string | null;
  updatedAt: Date | null;
  connection: ConnectionState;
  /** Status changes since the last render, for toasts. Consumed by the caller. */
  transitions: Transition[];
  refresh: () => Promise<void>;
  clearTransitions: () => void;
}

const POLL_MS = 30_000;

/**
 * Single source of dashboard data.
 *
 * Live updates arrive over SSE; a 30s poll runs regardless as a backstop,
 * because a proxy that silently drops the event stream would otherwise leave a
 * wall display frozen on stale data while looking perfectly healthy.
 */
export function useDashboard(): DashboardData {
  const [services, setServices] = useState<WireService[]>([]);
  const [openIncidents, setOpenIncidents] = useState<WireIncident[]>([]);
  const [incidents, setIncidents] = useState<WireIncident[]>([]);
  const [agents, setAgents] = useState<StatusResponse['agents']>([]);
  const [history, setHistory] = useState<HistoryResponse['history']>({});
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [transitions, setTransitions] = useState<Transition[]>([]);

  // Previous statuses, so toasts fire on genuine changes and never on a poll.
  const lastStatus = useRef(new Map<string, WireService['status']>());
  const loadedOnce = useRef(false);
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    // Overlapping refreshes would race and could apply an older snapshot last.
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const [status, incidentList, historyData] = await Promise.all([
        api.status(),
        api.incidents(),
        api.history(),
      ]);

      if (loadedOnce.current) {
        const changed: Transition[] = [];
        for (const svc of status.services) {
          const previous = lastStatus.current.get(svc.key);
          if (previous && previous !== svc.status) {
            changed.push({
              key: `${svc.key}-${Date.now()}`,
              serviceName: svc.name,
              from: previous,
              to: svc.status,
              at: Date.now(),
            });
          }
        }
        if (changed.length) setTransitions((prev) => [...prev, ...changed]);
      }
      for (const svc of status.services) lastStatus.current.set(svc.key, svc.status);
      loadedOnce.current = true;

      setServices(status.services);
      setOpenIncidents(status.openIncidents);
      setAgents(status.agents);
      setIncidents(incidentList.incidents);
      setHistory(historyData.history);
      setUpdatedAt(new Date());
      setError(null);
      setLoaded(true);
      setConnection((c) => (c === 'error' ? 'polling' : c));
    } catch (err) {
      // Surfaced in the header rather than swallowed: a dashboard showing stale
      // data as if it were live is worse than one admitting it lost contact.
      setError(err instanceof Error ? err.message : String(err));
      setConnection('error');
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = setInterval(() => void refresh(), POLL_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    const source = new EventSource('/api/v1/stream');
    const onChange = () => void refresh();

    source.onopen = () => setConnection('live');
    source.onerror = () => setConnection((c) => (c === 'error' ? c : 'polling'));
    source.addEventListener('check', onChange);
    source.addEventListener('incident', onChange);

    return () => {
      source.removeEventListener('check', onChange);
      source.removeEventListener('incident', onChange);
      source.close();
    };
  }, [refresh]);

  const clearTransitions = useCallback(() => setTransitions([]), []);

  return {
    services,
    openIncidents,
    incidents,
    agents,
    history,
    loaded,
    error,
    updatedAt,
    connection,
    transitions,
    refresh,
    clearTransitions,
  };
}

/**
 * Ticks every 15s so relative durations ("4m", "2h 10m") stay honest without
 * refetching. Returns a value only to force a re-render.
 */
export function useClockTick(intervalMs = 15_000): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return tick;
}
